import { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import TournamentCard from "./TournamentCard";
import { useEventsByType, useUpcomingEventsByType } from "@/hooks/useEvents";
import { parseLocalDate } from "@/utils/dateUtils";
import { useLanguage } from "@/contexts/LanguageContext";

interface ExploreEventsProps {
  onEventClick?: (event: any) => void;
  showSearchAndFilters?: boolean;
  maxEvents?: number;
  title?: string;
  externalSelectedSport?: string;
  onlyUpcoming?: boolean;
}

const ExploreEvents = ({ 
  onEventClick, 
  showSearchAndFilters = true, 
  maxEvents, 
  title,
  externalSelectedSport,
  onlyUpcoming = false
}: ExploreEventsProps) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSport, setSelectedSport] = useState("todos");
  const [selectedDistance, setSelectedDistance] = useState("todos");
  const [isDistanceFilterOpen, setIsDistanceFilterOpen] = useState(false);

  // Distance options
  const distanceOptions = [
    { value: "todos", label: "Todos", range: "Sin límite" },
    { value: "cercano", label: "Cercano", range: "≤5 km" },
    { value: "medio", label: "Medio", range: "≤10 km" },
    { value: "lejano", label: "Lejano", range: "≤25 km" }
  ];

  // Use real data from database - filter by upcoming status if needed
  const { events: allTournamentEvents, loading: loadingAll } = useEventsByType('tournament');
  const { events: upcomingTournamentEvents, loading: loadingUpcoming } = useUpcomingEventsByType('tournament');
  
  const tournamentEvents = onlyUpcoming ? upcomingTournamentEvents : allTournamentEvents;
  const loading = onlyUpcoming ? loadingUpcoming : loadingAll;
  
  // Map database events to component format
  const allEvents = tournamentEvents?.map(event => {
    return {
      id: event.id,
      title: event.title,
      sport: event.sport,
      date: event.start_date,
      endDate: event.end_date || '',
      time: new Date(event.start_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      start_time: event.start_time,
      end_time: event.end_time,
      game_days: event.game_days,
      location: event.location,
      participants: 0,
      maxParticipants: event.max_participants || 0,
      registrationFee: Number(event.entry_fee) || 0,
      categories: event.genderCategories || [],
      organizer: event.organizer_company_name || event.organizer_full_name || 'Organizador',
      genderCategories: event.genderCategories && event.genderCategories.length > 0
        ? event.genderCategories
        : event.subcategories && Object.keys(event.subcategories).length > 0
          ? Object.keys(event.subcategories)
          : event.gender_category
            ? event.gender_category.split(',').map(s => s.trim())
            : [],
      subcategories: event.subcategories && Object.keys(event.subcategories).length > 0
        ? event.subcategories
        : event.gender_category && event.category && event.subcategory
          ? { [`${event.gender_category.charAt(0).toUpperCase() + event.gender_category.slice(1)} ${event.category}`]: event.subcategory.split(',').map(s => s.trim()) }
          : {},
      matchType: (event.match_type as 'singles' | 'dobles' | 'ambos') || 'dobles',
      tournamentFormat: event.tournament_format as 'round-robin' | 'eliminacion-directa' | 'sumatoria-juegos',
      isLeague: false,
      registrationDeadline: event.registration_deadline || undefined,
      imageUrl: (event as any).image_url || undefined,
      address: event.address || undefined,
      postal_code: event.postal_code || undefined,
      country: (event as any).country || undefined,
      state: (event as any).state || undefined,
      organizer_id: event.organizer_id,
      prizes: []
    };
  }) || [];

  if (loading) {
    return <div className="text-center py-8">{t('events.loadingEvents')}</div>;
  }

  // Determine which sport filter to use
  const effectiveSelectedSport = externalSelectedSport || selectedSport;

  // Helper function to normalize sport names (case-insensitive, accent-insensitive)
  const normalizeSport = (sport: string): string => {
    return sport.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Filter events based on search, sport, and distance
  const filteredEvents = allEvents.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.sport.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Handle sport filtering with normalized comparison (case-insensitive, accent-insensitive)
    let matchesSport = true;
    if (externalSelectedSport && externalSelectedSport !== "todos") {
      matchesSport = normalizeSport(event.sport) === normalizeSport(externalSelectedSport);
    } else {
      matchesSport = selectedSport === "todos" || normalizeSport(event.sport) === normalizeSport(selectedSport);
    }
    
    let matchesDistance = true;
    if (selectedDistance !== "todos") {
      matchesDistance = true;
    }
    
    // Filter out events with closed registrations
    const calculateDaysUntilDeadline = (deadline: string) => {
      const today = new Date();
      const deadlineDate = new Date(deadline);
      const diffTime = deadlineDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };
    
    const hasOpenRegistrations = !event.registrationDeadline || calculateDaysUntilDeadline(event.registrationDeadline) >= 0;
    
    // Keep events visible until 1 day after they end
    const hasNotEndedYet = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const referenceDate = parseLocalDate(event.endDate || event.date);
      referenceDate.setHours(0, 0, 0, 0);
      return today <= referenceDate;
    };
    
    return matchesSearch && matchesSport && matchesDistance && hasNotEndedYet();
  });

  // Fixed list of available sports - always show all options
  const availableSports = ["todos", "Pádel", "Tenis", "Pickleball"];

  const handleDistanceSelect = (distance: string) => {
    setSelectedDistance(distance);
    setIsDistanceFilterOpen(false);
  };

  const getDistanceButtonText = () => {
    const selected = distanceOptions.find(option => option.value === selectedDistance);
    return selectedDistance === "todos" ? "Distancia" : selected?.range || "Distancia";
  };

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      {showSearchAndFilters && (
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder={t('explore.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 placeholder:text-xs placeholder:text-gray-400"
                  />
                </div>
              </div>
              
              {/* Sport Filter and Distance Filter */}
              <div className="flex gap-2 justify-center">
                {availableSports.map((sport) => (
                  <Button
                    key={sport}
                    variant={selectedSport === sport ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedSport(sport)}
                    className="capitalize text-xs md:text-sm h-6 md:h-8 px-2 md:px-4"
                  >
                    {sport === "todos" ? t('common.all') : sport}
                  </Button>
                ))}
                
                {/* Distance Filter */}
                <Sheet open={isDistanceFilterOpen} onOpenChange={setIsDistanceFilterOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant={selectedDistance !== "todos" ? "default" : "outline"}
                      size="sm"
                      className="flex items-center gap-2 text-xs md:text-sm h-6 md:h-8 px-2 md:px-4"
                    >
                      <MapPin className="h-4 w-4" />
                      <span className="hidden sm:inline">{getDistanceButtonText()}</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80">
                    <SheetHeader>
                      <SheetTitle>{t('events.filterByDistance')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                      {distanceOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleDistanceSelect(option.value)}
                          className={`w-full p-4 text-left rounded-lg border transition-colors ${
                            selectedDistance === option.value
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-sm text-gray-500">{option.range}</div>
                            </div>
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              selectedDistance === option.value
                                ? "border-primary bg-primary"
                                : "border-gray-300"
                            }`}>
                              {selectedDistance === option.value && (
                                <div className="w-full h-full rounded-full bg-white scale-50"></div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events Grid - Horizontal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(maxEvents ? filteredEvents.slice(0, maxEvents) : filteredEvents).length > 0 ? (
          (maxEvents ? filteredEvents.slice(0, maxEvents) : filteredEvents).map((event) => (
            <TournamentCard key={event.id} tournament={event} onTournamentClick={onEventClick} />
          ))
        ) : (
          <Card className="bg-white col-span-full">
            <CardContent className="p-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('events.noTournamentsFound')}</h3>
              <p className="text-gray-600">{t('events.adjustSearch')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExploreEvents;